import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import OnboardingVideo from "@/components/OnboardingVideo";
import { CATEGORIES } from "@/lib/constants";
import {
    Check,
    PlayCircle,
    Camera,
    Briefcase,
    FileText,
    IdCard,
    Building2,
    GraduationCap,
    Loader2,
    Upload,
    Clock,
    ShieldCheck,
    ArrowRight,
    ArrowLeft,
} from "lucide-react";

/**
 * CandidateOnboardingWizard — Feb 2026
 * Parcours d'onboarding candidat obligatoire en 6 étapes verrouillées.
 * L'admin dispose ensuite de 24h (indicatif) pour valider le dossier.
 */

const STEPS = [
    { key: "video", title: "Vidéo Renforteo", icon: PlayCircle },
    { key: "photo", title: "Photo de profil", icon: Camera },
    { key: "profile", title: "Ma demande", icon: Briefcase },
    { key: "cv", title: "Mon CV", icon: FileText },
    { key: "identity", title: "Papiers officiels", icon: IdCard },
    { key: "business", title: "Activité pro", icon: Building2 },
];

export default function CandidateOnboardingWizard() {
    const nav = useNavigate();
    const [status, setStatus] = useState(null);
    const [step, setStep] = useState(0);
    const [videoOpen, setVideoOpen] = useState(false);

    const refresh = async () => {
        try {
            const r = await api.get("/candidates/me/onboarding-status");
            setStatus(r.data);
            // If already validated, back to dashboard.
            if (["validated", "premium"].includes(r.data.profile_status)) {
                nav("/candidat", { replace: true });
                return;
            }
        } catch (e) {
            toast.error("Impossible de charger l'état de votre dossier");
        }
    };
    useEffect(() => { refresh(); }, []);

    const isSubmitted = useMemo(() => {
        if (!status) return false;
        return Boolean(status.submitted_for_review_at) || status.profile_status === "pending_verification";
    }, [status]);

    if (!status) {
        return (
            <div className="min-h-screen flex items-center justify-center text-slate-500">
                <Loader2 className="animate-spin mr-2" size={18} /> Chargement…
            </div>
        );
    }

    const checklist = status.checklist || {};
    const stepDone = [
        checklist.video_seen,
        checklist.has_photo,
        checklist.profile_filled,
        checklist.has_cv,
        checklist.has_id_card && checklist.has_health_card && checklist.has_rib,
        checklist.has_business_proof,
    ];

    return (
        <div className="bg-slate-50 min-h-screen">
            <Header />
            <div className="max-w-3xl mx-auto px-6 py-10">
                <div className="mb-8">
                    <div className="overline mb-2">Espace candidat · Onboarding</div>
                    <h1 className="font-display text-3xl md:text-4xl mb-2" data-testid="onboarding-title">
                        Complétez votre profil.
                    </h1>
                    <p className="text-slate-500 text-sm max-w-xl">
                        Toutes les étapes ci-dessous sont indispensables pour activer votre profil et postuler à des missions.
                        Notre équipe validera ensuite votre dossier sous <b>24h</b>.
                    </p>
                </div>

                <ProgressBar step={step} stepDone={stepDone} onGo={setStep} />

                {isSubmitted ? (
                    <SubmittedScreen status={status} />
                ) : (
                    <div className="sd-card p-6 md:p-8 mt-8" data-testid={`onboarding-step-${STEPS[step].key}`}>
                        <StepHeader step={step} />
                        <StepBody
                            step={step}
                            status={status}
                            refresh={refresh}
                            openVideo={() => setVideoOpen(true)}
                            goNext={() => setStep((s) => Math.min(s + 1, STEPS.length - 1))}
                            goPrev={() => setStep((s) => Math.max(s - 1, 0))}
                            done={stepDone[step]}
                        />
                        {step === STEPS.length - 1 && stepDone.every(Boolean) && (
                            <SubmitButton refresh={refresh} />
                        )}
                    </div>
                )}
            </div>
            {videoOpen && (
                <OnboardingVideo
                    onClose={async () => {
                        setVideoOpen(false);
                        try {
                            await api.post("/candidates/me/onboarding-video-seen");
                            await refresh();
                        } catch (_) {}
                    }}
                />
            )}
            <Footer />
        </div>
    );
}

// ------ Progress bar ------
function ProgressBar({ step, stepDone, onGo }) {
    return (
        <div className="grid grid-cols-6 gap-1" data-testid="onboarding-progress">
            {STEPS.map((s, i) => {
                const Icon = s.icon;
                const done = stepDone[i];
                const current = i === step;
                return (
                    <button
                        type="button"
                        key={s.key}
                        onClick={() => onGo(i)}
                        className={`text-left p-2 rounded-lg transition-colors border ${
                            done
                                ? "bg-emerald-50 border-emerald-300 text-emerald-800"
                                : current
                                ? "bg-[#0B192C] border-[#0B192C] text-white"
                                : "bg-white border-slate-200 text-slate-500"
                        }`}
                        data-testid={`step-btn-${s.key}`}
                    >
                        <div className="flex items-center justify-between">
                            <div className="text-[10px] uppercase tracking-widest opacity-70">
                                Étape {i + 1}
                            </div>
                            {done ? <Check size={12} /> : <Icon size={12} />}
                        </div>
                        <div className="text-[11px] font-semibold mt-1 truncate">{s.title}</div>
                    </button>
                );
            })}
        </div>
    );
}

function StepHeader({ step }) {
    const s = STEPS[step];
    const Icon = s.icon;
    return (
        <div className="flex items-center gap-3 mb-5">
            <div className="w-11 h-11 rounded-full bg-[#F5A623] text-[#0B192C] flex items-center justify-center">
                <Icon size={22} />
            </div>
            <div>
                <div className="overline">Étape {step + 1} / {STEPS.length}</div>
                <div className="font-display text-xl">{s.title}</div>
            </div>
        </div>
    );
}

function StepBody({ step, status, refresh, openVideo, goNext, goPrev, done }) {
    switch (STEPS[step].key) {
        case "video": return <VideoStep status={status} openVideo={openVideo} goNext={goNext} />;
        case "photo": return <PhotoStep status={status} refresh={refresh} goNext={goNext} goPrev={goPrev} done={done} />;
        case "profile": return <ProfileStep status={status} refresh={refresh} goNext={goNext} goPrev={goPrev} done={done} />;
        case "cv": return <DocStep type="cv" label="Curriculum vitae (PDF, DOC ou image)" hint="Un CV clair et à jour aide notre équipe à vous proposer les bonnes missions." refresh={refresh} goNext={goNext} goPrev={goPrev} done={done} />;
        case "identity": return <IdentityStep status={status} refresh={refresh} goNext={goNext} goPrev={goPrev} done={done} />;
        case "business": return <BusinessStep status={status} refresh={refresh} goPrev={goPrev} done={done} />;
        default: return null;
    }
}

// ---------- Step 1: Video ----------
function VideoStep({ status, openVideo, goNext }) {
    const seen = status.checklist.video_seen;
    return (
        <div>
            <p className="text-sm text-slate-600 mb-5 leading-relaxed">
                Avant de compléter votre profil, prenez <b>40 secondes</b> pour découvrir comment fonctionne Renforteo :
                inscription, validation, postulation, mission, paiement.
            </p>
            <button
                onClick={openVideo}
                className="w-full flex items-center gap-3 p-4 rounded-xl bg-gradient-to-br from-[#0B192C] to-[#132845] text-white text-left hover:shadow-lg transition-shadow"
                data-testid="watch-video-btn"
            >
                <div className="w-11 h-11 rounded-full bg-[#F5A623] text-[#0B192C] flex items-center justify-center shrink-0">
                    <PlayCircle size={26} />
                </div>
                <div className="flex-1">
                    <div className="text-xs uppercase tracking-widest text-[#F5A623] mb-0.5">Vidéo obligatoire</div>
                    <div className="font-semibold text-sm">Comment fonctionne Renforteo — 6 scènes, 40 sec.</div>
                    <div className="text-xs text-white/70 mt-0.5">Inscription · validation · postulation · mission · paiement</div>
                </div>
            </button>
            {seen && (
                <div className="mt-4 flex items-center gap-2 text-emerald-700 text-sm" data-testid="video-seen-marker">
                    <Check size={16} /> Vidéo vue. Vous pouvez passer à l'étape suivante.
                </div>
            )}
            <div className="flex justify-end mt-6">
                <button
                    className="btn-gold disabled:opacity-50"
                    disabled={!seen}
                    onClick={goNext}
                    data-testid="step-next-btn"
                >
                    Suivant <ArrowRight size={14} className="inline ml-1" />
                </button>
            </div>
        </div>
    );
}

// ---------- Step 2: Photo ----------
function PhotoStep({ status, refresh, goNext, goPrev, done }) {
    const [uploading, setUploading] = useState(false);
    const [preview, setPreview] = useState(null);
    const inputRef = useRef();
    const upload = async (file) => {
        if (!file) return;
        if (file.size > 3 * 1024 * 1024) {
            toast.error("Photo trop volumineuse (max 3 Mo)");
            return;
        }
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            await api.post("/candidates/photo", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            const url = URL.createObjectURL(file);
            setPreview(url);
            toast.success("Photo enregistrée");
            await refresh();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Erreur d'upload");
        } finally {
            setUploading(false);
        }
    };
    return (
        <div>
            <p className="text-sm text-slate-600 mb-5">
                Une photo claire et professionnelle (visage bien visible) rassure les clients et améliore vos chances d'être sélectionné.
            </p>
            <div className="flex items-center gap-6 flex-wrap">
                <div className="w-28 h-28 rounded-2xl bg-slate-100 border-2 border-dashed border-slate-300 flex items-center justify-center overflow-hidden">
                    {preview ? (
                        <img src={preview} alt="" className="w-full h-full object-cover" />
                    ) : done ? (
                        <div className="text-emerald-600 flex flex-col items-center gap-1">
                            <Check size={26} />
                            <span className="text-[10px] font-semibold">Photo enregistrée</span>
                        </div>
                    ) : (
                        <Camera size={30} className="text-slate-400" />
                    )}
                </div>
                <div>
                    <input
                        ref={inputRef}
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        className="hidden"
                        onChange={(e) => upload(e.target.files?.[0])}
                        data-testid="photo-input"
                    />
                    <button
                        onClick={() => inputRef.current?.click()}
                        disabled={uploading}
                        className="btn-navy disabled:opacity-50"
                        data-testid="photo-upload-btn"
                    >
                        {uploading ? (<><Loader2 size={14} className="inline animate-spin mr-2" />Envoi…</>) : done ? "Remplacer la photo" : "Choisir une photo"}
                    </button>
                    <div className="text-[11px] text-slate-500 mt-2">JPG, PNG ou WEBP · 3 Mo max</div>
                    {done && <div className="mt-2 flex items-center gap-2 text-emerald-700 text-sm"><Check size={14} /> Photo enregistrée</div>}
                </div>
            </div>
            <StepNav goPrev={goPrev} goNext={goNext} done={done} />
        </div>
    );
}

// ---------- Step 3: Profile / "Ma demande" ----------
function ProfileStep({ status, refresh, goNext, goPrev, done }) {
    const [profile, setProfile] = useState(null);
    const [saving, setSaving] = useState(false);
    useEffect(() => {
        api.get("/candidates/me").then((r) => setProfile(r.data)).catch(() => {});
    }, []);
    if (!profile) return <div className="text-sm text-slate-400"><Loader2 size={14} className="inline animate-spin mr-2" />Chargement…</div>;

    const save = async () => {
        if (!profile.primary_job) return toast.error("Choisissez votre métier principal");
        if (!(profile.job_request || "").trim()) return toast.error("Décrivez votre demande en quelques mots");
        setSaving(true);
        try {
            await api.put("/candidates/me", {
                primary_job: profile.primary_job,
                secondary_jobs: profile.secondary_jobs || [],
                job_request: profile.job_request,
                mobility_km: parseInt(profile.mobility_km) || 20,
                city: profile.city || "",
                bio: profile.bio || "",
            });
            toast.success("Profil enregistré");
            await refresh();
            goNext();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Erreur d'enregistrement");
        } finally {
            setSaving(false);
        }
    };

    const toggleSecondary = (key) => {
        const cur = new Set(profile.secondary_jobs || []);
        if (cur.has(key)) cur.delete(key);
        else cur.add(key);
        setProfile({ ...profile, secondary_jobs: Array.from(cur) });
    };

    return (
        <div className="space-y-4">
            <p className="text-sm text-slate-600">
                Précisez le métier que vous souhaitez exercer et détaillez votre demande (disponibilités, préférences, expérience…).
            </p>
            <div>
                <label className="block text-sm font-semibold mb-1">Métier principal *</label>
                <select
                    value={profile.primary_job || ""}
                    onChange={(e) => setProfile({ ...profile, primary_job: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-white"
                    data-testid="wizard-primary-job-select"
                >
                    <option value="">— Sélectionner —</option>
                    {CATEGORIES.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                </select>
            </div>
            <div>
                <label className="block text-sm font-semibold mb-2">
                    Autres métiers que je maîtrise
                    <span className="text-xs font-normal text-slate-400 ml-2">
                        (facultatif — cochez tous les métiers où vous avez de l'expérience pour recevoir plus de missions)
                    </span>
                </label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2" data-testid="wizard-secondary-jobs-grid">
                    {CATEGORIES.filter((c) => c.key !== profile.primary_job).map((c) => {
                        const checked = (profile.secondary_jobs || []).includes(c.key);
                        return (
                            <label
                                key={c.key}
                                className={`flex items-start gap-2 rounded-lg border px-3 py-2 cursor-pointer text-xs transition-colors ${
                                    checked ? "border-[#F5A623] bg-amber-50" : "border-slate-200 bg-white hover:border-slate-300"
                                }`}
                            >
                                <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={() => toggleSecondary(c.key)}
                                    className="mt-0.5 accent-[#F5A623]"
                                    data-testid={`wizard-secondary-job-${c.key}`}
                                />
                                <span>{c.label}</span>
                            </label>
                        );
                    })}
                </div>
                {(profile.secondary_jobs || []).length > 0 && (
                    <div className="mt-2 text-[11px] text-emerald-700 flex items-center gap-1">
                        <Check size={12} /> {(profile.secondary_jobs || []).length} métier(s) additionnel(s) sélectionné(s)
                    </div>
                )}
            </div>
            <div>
                <label className="block text-sm font-semibold mb-1">
                    Ma demande / mes précisions *
                    <span className="text-xs font-normal text-slate-400 ml-2">(disponibilités, expérience, préférences…)</span>
                </label>
                <textarea
                    rows={4}
                    value={profile.job_request || ""}
                    onChange={(e) => setProfile({ ...profile, job_request: e.target.value })}
                    placeholder="Ex : Disponible en soirées et weekends, 3 ans d'expérience en service en salle, préfère événements haut de gamme, permis B + véhicule…"
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-white text-sm"
                    data-testid="wizard-job-request-textarea"
                    maxLength={1200}
                />
                <div className="text-[10px] text-slate-400 text-right mt-1">{(profile.job_request || "").length} / 1200</div>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-semibold mb-1">Ville</label>
                    <input
                        value={profile.city || ""}
                        onChange={(e) => setProfile({ ...profile, city: e.target.value })}
                        placeholder="Lyon, Paris, Marseille…"
                        className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-white"
                        data-testid="wizard-city-input"
                    />
                </div>
                <div>
                    <label className="block text-sm font-semibold mb-1">Mobilité (km)</label>
                    <input
                        type="number"
                        min={0}
                        max={200}
                        value={profile.mobility_km || 20}
                        onChange={(e) => setProfile({ ...profile, mobility_km: parseInt(e.target.value) || 0 })}
                        className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-white"
                        data-testid="wizard-mobility-input"
                    />
                </div>
            </div>
            <div className="flex justify-between mt-6">
                <button onClick={goPrev} className="btn-ghost-navy" data-testid="step-prev-btn"><ArrowLeft size={14} className="inline mr-1" />Retour</button>
                <button
                    onClick={save}
                    disabled={saving}
                    className="btn-gold disabled:opacity-50"
                    data-testid="wizard-save-profile-btn"
                >
                    {saving ? "Enregistrement…" : (done ? "Mettre à jour et continuer" : "Enregistrer et continuer")}
                    <ArrowRight size={14} className="inline ml-1" />
                </button>
            </div>
        </div>
    );
}

// ---------- Reusable file upload for a single doc_type ----------
function DocUpload({ type, label, hint, refresh, testIdPrefix = "" }) {
    const inputRef = useRef();
    const [uploading, setUploading] = useState(false);
    const [existing, setExisting] = useState([]);
    const load = async () => {
        try {
            const r = await api.get("/candidates/documents");
            setExisting((r.data || []).filter((d) => d.doc_type === type));
        } catch (_) {}
    };
    useEffect(() => { load(); }, [type]);

    const upload = async (file) => {
        if (!file) return;
        if (file.size > 5 * 1024 * 1024) return toast.error("Fichier trop volumineux (max 5 Mo)");
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("doc_type", type);
            fd.append("file", file);
            await api.post("/candidates/documents", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success("Document téléversé");
            await load();
            await refresh();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Erreur d'upload");
        } finally {
            setUploading(false);
        }
    };

    const has = existing.length > 0;
    return (
        <div className={`rounded-xl border p-4 ${has ? "border-emerald-300 bg-emerald-50/40" : "border-slate-200 bg-white"}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                    <div className="font-semibold text-sm flex items-center gap-2">
                        {has && <Check size={14} className="text-emerald-600" />}
                        {label}
                    </div>
                    {hint && <div className="text-[11px] text-slate-500 mt-0.5">{hint}</div>}
                </div>
                <button
                    onClick={() => inputRef.current?.click()}
                    disabled={uploading}
                    className={`text-xs font-semibold px-3 py-1.5 rounded-full border ${has ? "border-emerald-300 text-emerald-800 bg-white" : "border-[#0B192C] text-[#0B192C]"} disabled:opacity-50`}
                    data-testid={`${testIdPrefix}wizard-upload-${type}`}
                >
                    {uploading ? <Loader2 size={12} className="inline animate-spin mr-1" /> : <Upload size={12} className="inline mr-1" />}
                    {has ? "Remplacer" : "Téléverser"}
                </button>
                <input
                    ref={inputRef}
                    type="file"
                    accept="application/pdf,image/jpeg,image/png,image/webp"
                    className="hidden"
                    onChange={(e) => upload(e.target.files?.[0])}
                    data-testid={`${testIdPrefix}wizard-file-input-${type}`}
                />
            </div>
            {has && (
                <div className="mt-2 text-[11px] text-slate-500 truncate">
                    {existing[0].original_filename} · statut :{" "}
                    {existing[0].status === "verified" ? "✅ vérifié" : existing[0].status === "rejected" ? "❌ refusé" : "⏳ en attente"}
                </div>
            )}
        </div>
    );
}

// ---------- Step 4: CV ----------
function DocStep({ type, label, hint, refresh, goNext, goPrev, done }) {
    return (
        <div className="space-y-4">
            <DocUpload type={type} label={label} hint={hint} refresh={refresh} />
            <StepNav goPrev={goPrev} goNext={goNext} done={done} />
        </div>
    );
}

// ---------- Step 5: Papiers officiels ----------
function IdentityStep({ status, refresh, goNext, goPrev, done }) {
    return (
        <div className="space-y-3">
            <p className="text-sm text-slate-600 mb-2">
                Ces 4 documents sont <b>obligatoires</b> pour valider votre profil et éditer vos contrats.
                Ils sont stockés de manière chiffrée et vérifiés automatiquement par IA (Gemini) pour garantir
                leur authenticité.
            </p>
            <DocUpload
                type="id_card"
                label="Carte d'identité — RECTO"
                hint="Face avant : photo + nom + prénom. CNI, passeport ou carte de séjour. Vérif IA automatique."
                refresh={refresh}
            />
            <DocUpload
                type="id_card_back"
                label="Carte d'identité — VERSO"
                hint="Face arrière : signature + adresse + zone MRZ (les 2 lignes de code < <). Vérif IA automatique."
                refresh={refresh}
            />
            <DocUpload
                type="health_card"
                label="Carte vitale"
                hint="Recto uniquement, numéro sécurité sociale visible. Vérif IA automatique."
                refresh={refresh}
            />
            <DocUpload
                type="rib"
                label="RIB / IBAN"
                hint="Nécessaire pour recevoir vos paiements. IBAN FR + BIC visibles. Vérif IA automatique."
                refresh={refresh}
            />
            <StepNav goPrev={goPrev} goNext={goNext} done={done} />
        </div>
    );
}

// ---------- Step 6: Business proof ----------
function BusinessStep({ status, refresh, goPrev, done }) {
    const [choice, setChoice] = useState("kbis");
    return (
        <div className="space-y-4">
            <p className="text-sm text-slate-600">
                Prouvez votre statut d'auto-entrepreneur / indépendant. Vous pouvez téléverser au choix votre <b>KBIS</b> (extrait société)
                ou votre <b>fiche INSEE</b> (SIRENE / auto-entrepreneur).
            </p>
            <div className="flex gap-2">
                {[
                    { k: "kbis", label: "KBIS", icon: Building2 },
                    { k: "insee", label: "Fiche INSEE", icon: GraduationCap },
                ].map((o) => (
                    <button
                        key={o.k}
                        onClick={() => setChoice(o.k)}
                        className={`px-4 py-2 rounded-full text-sm font-semibold border transition-colors flex items-center gap-2 ${
                            choice === o.k ? "bg-[#0B192C] text-white border-[#0B192C]" : "bg-white border-slate-200 text-slate-500"
                        }`}
                        data-testid={`business-choice-${o.k}`}
                    >
                        <o.icon size={14} /> {o.label}
                    </button>
                ))}
            </div>
            {choice === "kbis" ? (
                <DocUpload type="kbis" label="Extrait KBIS" hint="Datant de moins de 3 mois." refresh={refresh} />
            ) : (
                <DocUpload type="insee" label="Fiche INSEE / SIRENE" hint="Avis de situation ou attestation SIRENE." refresh={refresh} />
            )}
            <StepNav goPrev={goPrev} done={done} isLast />
        </div>
    );
}

// ---------- Nav buttons ----------
function StepNav({ goPrev, goNext, done, isLast }) {
    return (
        <div className="flex justify-between mt-6">
            <button onClick={goPrev} className="btn-ghost-navy" data-testid="step-prev-btn">
                <ArrowLeft size={14} className="inline mr-1" />Retour
            </button>
            {!isLast && (
                <button
                    onClick={goNext}
                    disabled={!done}
                    className="btn-gold disabled:opacity-50"
                    data-testid="step-next-btn"
                >
                    Suivant <ArrowRight size={14} className="inline ml-1" />
                </button>
            )}
        </div>
    );
}

// ---------- Final submit button (only when all steps done) ----------
function SubmitButton({ refresh }) {
    const [loading, setLoading] = useState(false);
    const submit = async () => {
        setLoading(true);
        try {
            await api.post("/candidates/me/submit-review");
            toast.success("Dossier envoyé — validation sous 24h");
            await refresh();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Erreur lors de la soumission");
        } finally {
            setLoading(false);
        }
    };
    return (
        <div className="mt-8 border-t border-slate-200 pt-6">
            <div className="rounded-xl bg-emerald-50 border-l-4 border-emerald-500 p-4 mb-4">
                <div className="flex items-center gap-2 text-sm font-semibold text-emerald-900 mb-1">
                    <Check size={16} /> Toutes les étapes sont complètes.
                </div>
                <p className="text-xs text-emerald-800/80">
                    En cliquant ci-dessous, votre dossier sera envoyé à notre équipe pour validation manuelle.
                    Vous recevrez un e-mail de confirmation dès que votre profil sera activé (délai indicatif : 24h ouvrées).
                </p>
            </div>
            <button
                onClick={submit}
                disabled={loading}
                className="btn-gold w-full disabled:opacity-50"
                data-testid="submit-review-btn"
            >
                {loading ? (<><Loader2 size={16} className="inline animate-spin mr-2" />Envoi…</>) : "Soumettre mon dossier pour validation (24h)"}
            </button>
        </div>
    );
}

// ---------- After submission ----------
function SubmittedScreen({ status }) {
    const nav = useNavigate();
    const hours = status.hours_since_submit;
    return (
        <div className="sd-card p-8 mt-8 text-center" data-testid="onboarding-submitted-screen">
            <div className="w-16 h-16 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center mx-auto mb-4">
                <ShieldCheck size={30} />
            </div>
            <h2 className="font-display text-2xl mb-2">Dossier reçu — validation en cours</h2>
            <p className="text-slate-600 max-w-xl mx-auto text-sm mb-4">
                Merci ! Votre dossier est arrivé en bonne et due forme. Notre équipe examine chaque profil manuellement pour garantir la qualité de la plateforme.
            </p>
            <div className="inline-flex items-center gap-2 bg-amber-50 border border-amber-200 text-amber-900 rounded-full px-4 py-1.5 text-xs font-semibold">
                <Clock size={12} />
                Délai indicatif : 24h ouvrées
                {typeof hours === "number" && hours >= 0 && (
                    <span className="ml-1 opacity-70">· envoyé il y a {hours}h</span>
                )}
            </div>
            <div className="mt-6">
                <button
                    onClick={() => nav("/candidat")}
                    className="btn-navy"
                    data-testid="back-to-dashboard-btn"
                >
                    Retour à mon espace
                </button>
            </div>
        </div>
    );
}
